import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Spinner } from 'react-bootstrap';
import { PaymentMethodRequest, SetupIntentPaymentMethodRequest } from '../../../../structures/requests/paymentMethod';
import { fetchClientSecret, cancelSetupIntent } from '../../../../utils/ajax/paymentMethodsAjax';
import { SetupIntentData } from '../../../../structures/models/paymentMethod';
import StripeSetupCardForm from './StripeSetupCardForm';

interface Props {
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>,
  isProcessing: boolean,
  handleSubmit: (data: SetupIntentPaymentMethodRequest | PaymentMethodRequest) => Promise<void>,
  setErrors: React.Dispatch<React.SetStateAction<string[]>>,
}

const StripeSetupCard: React.FC<Props> = ({
  setShowForm,
  isProcessing,
  handleSubmit,
  setErrors,
}) => {
  // Use ref to avoid recreating the `Stripe` object on every render.
  // Cf. https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
  const stripeRef = useRef<Promise<Stripe | null>>(null);
  if (stripeRef.current === null) {
    // @ts-ignore
    stripeRef.current = loadStripe(window.stripeKey || '');
  }

  const stripePromise = stripeRef.current;

  const [setupIntentData, setSetupIntentData] = useState<SetupIntentData|null>(null);

  const onCancel = useCallback(async () => {
    // Cancel unused SetupIntent, if exists
    if (setupIntentData?.stripe_setup_intent_id) {
      cancelSetupIntent(setupIntentData.stripe_setup_intent_id)
        .catch((errors) => {
          setErrors(errors);
        });
    }
    setShowForm(false);
  }, [setupIntentData]);

  useEffect(() => {
    // Fetch client secret when component loads
    fetchClientSecret()
      .then((data) => {
        setSetupIntentData(data);
      })
      .catch((errors) => {
        setErrors(errors);
      });
  }, []);

  if (!setupIntentData) {
    return (
      <Spinner animation="border" variant="primary" />
    );
  }

  const elementOptions = {
    clientSecret: setupIntentData.stripe_client_secret,
  };

  return (
    <Elements
      stripe={stripePromise}
      options={elementOptions}
    >
      <StripeSetupCardForm
        clientSecret={setupIntentData.stripe_client_secret}
        isProcessing={isProcessing}
        onConfirmCardSetup={handleSubmit}
        onCancel={onCancel}
        setErrors={setErrors}
      />
    </Elements>
  );
};

export default StripeSetupCard;
